import { lazy, Suspense } from 'react'
import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  RouterProvider,
} from 'react-router'

// Components
import { Loading } from './components/Loading'
import Icons from './icons/icons'
// App selector page
import { AppSelectorLayout } from './layouts/AppSelector'
/* Import layouts */
import { AuthAreaLayout } from './layouts/AuthArea'
import { BaseLayout } from './layouts/Base'
import { WizardLayout } from './layouts/Wizard'
/* Import pages */
// Auth pages
import { IndexPage } from './pages'
import { NotFoundPage } from './pages/404'
import { AuthPage } from './pages/auth'
import { SSOLoginPage } from './pages/auth/login-sso'
import LogoutPage from './pages/auth/logout'
import { ResetPasswordPage } from './pages/auth/resetPassword'
import { ConceptsCarPage } from './pages/carkulka/concepts'
import { ContractsCarPage } from './pages/carkulka/contracts'
// Carkulka pages
import { DashboardPage as CarkulkaDashboardPage } from './pages/carkulka/dashboard'
import { Step1 as CarkulkaStep1 } from './pages/carkulka/wizard/1'
import { Step2 as CarkulkaStep2 } from './pages/carkulka/wizard/2'
import { Step3 as CarkulkaStep3 } from './pages/carkulka/wizard/3'
import { Step4 as CarkulkaStep4 } from './pages/carkulka/wizard/4'
import { Step5 as CarkulkaStep5 } from './pages/carkulka/wizard/5'
import { CarkulkaSimulateStepTooFar } from './pages/carkulka/wizard/too-far'
// Carkulka Extra pages
import { Step1 as CarkulkaExtraStep1 } from './pages/carkulka-extra/wizard/1'
import { Step2 as CarkulkaExtraStep2 } from './pages/carkulka-extra/wizard/2'
import { Step3 as CarkulkaExtraStep3 } from './pages/carkulka-extra/wizard/3'
import { Step4 as CarkulkaExtraStep4 } from './pages/carkulka-extra/wizard/4'
import { ConceptsCestovkoPage } from './pages/cestovko/concepts'
import { ContractsCestovkoPage } from './pages/cestovko/contracts'
// Cestovko pages
import { DashboardPage as CestovkoDashboardPage } from './pages/cestovko/dashboard'
import { Step1 as CestovkoStep1 } from './pages/cestovko/wizard/1'
import { Step2 as CestovkoStep2 } from './pages/cestovko/wizard/2'
import { Step3 as CestovkoStep3 } from './pages/cestovko/wizard/3'
import { Step4 as CestovkoStep4 } from './pages/cestovko/wizard/4'
import { Step5 as CestovkoStep5 } from './pages/cestovko/wizard/5'
import { ConceptsPropertyPage } from './pages/chaloupka/concepts'
import { ContractsPropertyPage } from './pages/chaloupka/contracts'
// Chaloupka pages
import { DashboardPage as ChaloupkaDashboardPage } from './pages/chaloupka/dashboard'
import { Step1 as ChaloupkaStep1 } from './pages/chaloupka/wizard/1'
import { Step2 as ChaloupkaStep2 } from './pages/chaloupka/wizard/2'
import { Step3 as ChaloupkaStep3 } from './pages/chaloupka/wizard/3'
import { Step4 as ChaloupkaStep4 } from './pages/chaloupka/wizard/4'
import { Step5 as ChaloupkaStep5 } from './pages/chaloupka/wizard/5'
import { CompletedPage as ChaloupkaCompletedPage } from './pages/chaloupka/wizard/completed'
import { ErrorPage } from './pages/error'
import { FAQAdmin } from './pages/faq/FAQAdmin'
import { FAQPage } from './pages/faq/FAQPage'
// FlexiGroup pages
import { FlexGroupCreatePage } from './pages/flex-group/create'
import { FlexGroupDetailPage } from './pages/flex-group/detail'
import { FlexGroupEditPage } from './pages/flex-group/edit'
import { FlexGroupListPage } from './pages/flex-group/list'
// News pages
import { NewsAdmin } from './pages/news/admin'
import { NewsDetailPage } from './pages/news/NewsDetailPage'
import { NewsListPage } from './pages/news/NewsListPage'
// Organization pages
import { OrganizationCreatePage } from './pages/organization/create'
import { OrganizationCreateDependentPage } from './pages/organization/createDependent'
import { OrganizationCreateUserPage } from './pages/organization/createUser'
import { OrganizationCurrentPage } from './pages/organization/current'
import { OrganizationDetailPage } from './pages/organization/detail'
import { OrganizationEditPage } from './pages/organization/edit'
import { OrganizationListPage } from './pages/organization/list'
// Profile and user pages
import { ProfilePage } from './pages/profile'
import { ProfileChangePasswordPage } from './pages/profile/changePassword'
// RTPC pages
import { RtpcPage } from './pages/rtpc'
import { SetRtpcOrganizationsPage } from './pages/rtpc/setOrganizations'
// Sme pages
import { AuthLayout } from './layouts/Auth'
import { AdminLoginPage } from './pages/auth/admin'
import { DashboardPage as SmeDashboardPage } from './pages/sme/dashboard'
import { Step1 as SmeStep1 } from './pages/sme/wizard/1'
import { Step2 as SmeStep2 } from './pages/sme/wizard/2'
import { Step3 as SmeStep3 } from './pages/sme/wizard/3'
import { Step4 as SmeStep4 } from './pages/sme/wizard/4'
import { Step5 as SmeStep5 } from './pages/sme/wizard/5'
import { CompletedPage as SmeCompletedPage } from './pages/sme/wizard/completed'
import { DiscountsOverviewPage } from './pages/supervizor/discountsOverview'
import UnauthorizedPage from './pages/Unauthorized'
import { UserDetailPage } from './pages/user/detail'
import { UserEditPage } from './pages/user/edit'
import { UserListPage } from './pages/user/list'

const NewsNewItem = lazy(() => import('./pages/news/NewsNewItem'))

export const Router = () => {
  const routes = createRoutesFromElements(
    <Route errorElement={<ErrorPage />}>
      <Route element={<AuthLayout />}>
        <Route path="/auth" element={<AuthAreaLayout />}>
          <Route index element={<AuthPage />} />
          <Route path="/auth/admin" element={<AdminLoginPage />} />
          <Route path="/auth/login-sso" element={<SSOLoginPage />} />
          <Route path="/auth/logout" element={<LogoutPage />} />
          <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
        </Route>
        <Route
          path="/carkulka/extra"
          element={<WizardLayout app="carkulka-extra" />}
        >
          <Route
            path="/carkulka/extra/wizard/1"
            element={<CarkulkaExtraStep1 />}
          />
          <Route
            path="/carkulka/extra/wizard/1/:insuranceParamsId"
            element={<CarkulkaExtraStep1 />}
          />
          <Route
            path="/carkulka/extra/wizard/2/:insuranceParamsId"
            element={<CarkulkaExtraStep2 />}
          />
          <Route
            path="/carkulka/extra/wizard/3/:insuranceParamsId"
            element={<CarkulkaExtraStep3 />}
          />
          <Route
            path="/carkulka/extra/wizard/4/:insuranceParamsId"
            element={<CarkulkaExtraStep4 />}
          />
          {/* <Route path="/carkulka/extra/wizard/completed" element={<CarkulkaExtraCompletedPage />} /> */}
          <Route
            path="/carkulka/extra/wizard/too-far"
            element={<CarkulkaSimulateStepTooFar />}
          />
        </Route>
        <Route path="/carkulka/wizard" element={<WizardLayout app="carkulka" />}>
          <Route path="/carkulka/wizard/1" element={<CarkulkaStep1 />} />
          <Route
            path="/carkulka/wizard/1/:insuranceParamsId"
            element={<CarkulkaStep1 />}
          />
          <Route
            path="/carkulka/wizard/2/:insuranceParamsId"
            element={<CarkulkaStep2 />}
          />
          <Route
            path="/carkulka/wizard/3/:insuranceParamsId"
            element={<CarkulkaStep3 />}
          />
          <Route
            path="/carkulka/wizard/4/:insuranceParamsId"
            element={<CarkulkaStep4 />}
          />
          <Route
            path="/carkulka/wizard/5/:insuranceParamsId"
            element={<CarkulkaStep5 />}
          />
          {/* <Route path="/carkulka/wizard/completed/:insuranceParamsId" element={<CarkulkaCompletedPage />} /> */}
          <Route
            path="/carkulka/wizard/too-far"
            element={<CarkulkaSimulateStepTooFar />}
          />
        </Route>
        <Route path="/carkulka" element={<BaseLayout app="carkulka" />}>
          <Route path="/carkulka/concepts" element={<ConceptsCarPage />} />
          <Route path="/carkulka/contracts" element={<ContractsCarPage />} />
          <Route path="/carkulka" element={<CarkulkaDashboardPage />} />
        </Route>
        <Route path="/chaloupka" element={<WizardLayout app="chaloupka" />}>
          <Route path="/chaloupka/wizard/1" element={<ChaloupkaStep1 />} />
          <Route
            path="/chaloupka/wizard/1/:insuranceParamsId"
            element={<ChaloupkaStep1 />}
          />
          <Route
            path="/chaloupka/wizard/2/:insuranceParamsId"
            element={<ChaloupkaStep2 />}
          />
          <Route
            path="/chaloupka/wizard/3/:insuranceParamsId"
            element={<ChaloupkaStep3 />}
          />
          <Route
            path="/chaloupka/wizard/4/:insuranceParamsId"
            element={<ChaloupkaStep4 />}
          />
          <Route
            path="/chaloupka/wizard/5/:insuranceParamsId"
            element={<ChaloupkaStep5 />}
          />
          <Route
            path="/chaloupka/wizard/completed"
            element={<ChaloupkaCompletedPage />}
          />
          <Route path="/chaloupka/concepts" element={<ConceptsPropertyPage />} />
          <Route
            path="/chaloupka/contracts"
            element={<ContractsPropertyPage />}
          />
          <Route path="/chaloupka" element={<ChaloupkaDashboardPage />} />
          <Route
            path="/chaloupka/wizard/too-far"
            element={<CarkulkaSimulateStepTooFar />}
          />
        </Route>
        <Route path="/sme" element={<BaseLayout app="sme" />}>
          <Route path="/sme/wizard/1" element={<SmeStep1 />} />
          <Route path="/sme/wizard/2" element={<SmeStep2 />} />
          <Route path="/sme/wizard/3" element={<SmeStep3 />} />
          <Route path="/sme/wizard/4" element={<SmeStep4 />} />
          <Route path="/sme/wizard/5" element={<SmeStep5 />} />
          <Route path="/sme/wizard/completed" element={<SmeCompletedPage />} />
          <Route path="/sme/concepts" element={<ConceptsPropertyPage />} />
          <Route path="/sme/contracts" element={<ContractsPropertyPage />} />
          <Route path="/sme" element={<SmeDashboardPage />} />
        </Route>
        <Route path="/cestovko" element={<WizardLayout app="cestovko" />}>
          <Route path="/cestovko/wizard/1" element={<CestovkoStep1 />} />
          <Route
            path="/cestovko/wizard/1/:insuranceParamsId"
            element={<CestovkoStep1 />}
          />
          <Route
            path="/cestovko/wizard/2/:insuranceParamsId"
            element={<CestovkoStep2 />}
          />
          <Route
            path="/cestovko/wizard/3/:insuranceParamsId"
            element={<CestovkoStep3 />}
          />
          <Route
            path="/cestovko/wizard/4/:insuranceParamsId"
            element={<CestovkoStep4 />}
          />
          <Route
            path="/cestovko/wizard/5/:insuranceParamsId"
            element={<CestovkoStep5 />}
          />
          {/* <Route path="/cestovko/wizard/completed" element={<CestovkoCompletedPage />} /> */}
          <Route
            path="/cestovko/wizard/too-far"
            element={<CarkulkaSimulateStepTooFar />}
          />
          <Route path="/cestovko/concepts" element={<ConceptsCestovkoPage />} />
          <Route path="/cestovko/contracts" element={<ContractsCestovkoPage />} />
          <Route path="/cestovko" element={<CestovkoDashboardPage />} />
        </Route>
        <Route path="/profile" element={<BaseLayout app="carkulka" />}>
          <Route path="/profile" element={<ProfilePage />} />
          <Route
            path="/profile/change-password"
            element={<ProfileChangePasswordPage />}
          />
        </Route>
        <Route path="/user" element={<BaseLayout app="carkulka" />}>
          <Route path="/user/list" element={<UserListPage />} />
          <Route path="/user/:userId" element={<UserDetailPage />} />
          <Route path="/user/:userId/edit" element={<UserEditPage />} />
        </Route>
        <Route path="/organization" element={<BaseLayout app="carkulka" />}>
          <Route path="/organization" element={<OrganizationCurrentPage />} />
          <Route path="/organization/list" element={<OrganizationListPage />} />
          <Route
            path="/organization/create"
            element={<OrganizationCreatePage />}
          />
          <Route
            path="/organization/:organizationUnitId"
            element={<OrganizationDetailPage />}
          />
          <Route
            path="/organization/:organizationUnitId/edit"
            element={<OrganizationEditPage />}
          />
          <Route
            path="/organization/:organizationUnitId/createDependent"
            element={<OrganizationCreateDependentPage />}
          />
          <Route
            path="/organization/:organizationUnitId/createUser"
            element={<OrganizationCreateUserPage />}
          />
        </Route>
        <Route path="/flex-group" element={<BaseLayout app="carkulka" />}>
          <Route path="/flex-group/list" element={<FlexGroupListPage />} />
          <Route
            path="/flex-group/:flexGroupId"
            element={<FlexGroupDetailPage />}
          />
          <Route path="/flex-group/create" element={<FlexGroupCreatePage />} />
          <Route
            path="/flex-group/:flexGroupId/edit"
            element={<FlexGroupEditPage />}
          />
        </Route>
        <Route path="/icons" element={<BaseLayout app="carkulka" />}>
          <Route path="/icons" element={<Icons />} />
        </Route>
        <Route path="/carkulka/news" element={<BaseLayout app="carkulka" />}>
          <Route path="/carkulka/news/:id" element={<NewsDetailPage />} />
          <Route path="/carkulka/news" element={<NewsListPage />} />
        </Route>
        <Route path="/cestovko/news" element={<BaseLayout app="cestovko" />}>
          <Route path="/cestovko/news/:id" element={<NewsDetailPage />} />
          <Route path="/cestovko/news" element={<NewsListPage />} />
        </Route>
        <Route path="/chaloupka/news" element={<BaseLayout app="chaloupka" />}>
          <Route path="/chaloupka/news/:id" element={<NewsDetailPage />} />
          <Route path="/chaloupka/news" element={<NewsListPage />} />
        </Route>
        <Route path="/news" element={<BaseLayout app={null} />}>
          <Route path="/news/admin" element={<NewsAdmin />} />
          <Route
            path="/news/new"
            element={
              <Suspense fallback={<Loading large />}>
                <NewsNewItem />
              </Suspense>
            }
          />
          <Route
            path="/news/new/:id"
            element={
              <Suspense fallback={<Loading large />}>
                <NewsNewItem />
              </Suspense>
            }
          />
        </Route>
        <Route path="/supervizor" element={<BaseLayout app="carkulka" />}>
          <Route
            path="/supervizor/discounts"
            element={<DiscountsOverviewPage />}
          />
        </Route>
        <Route path="/faq" element={<BaseLayout app="carkulka" />}>
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/faq/admin" element={<FAQAdmin />} />
        </Route>
        <Route path="/" element={<AppSelectorLayout />}>
          <Route index element={<IndexPage />} />
        </Route>
        <Route path="/rtpc" element={<BaseLayout app="carkulka" />}>
          <Route index element={<RtpcPage />} />
          <Route
            path="/rtpc/:rtpcId/setOrganizations"
            element={<SetRtpcOrganizationsPage />}
          />
        </Route>
        <Route path="/Account/ProcessLogin" element={<RetirectToLogin />} />
        <Route path="/unauthorized" element={<UnauthorizedPage />} />
        <Route path="/apperror" element={<ErrorPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Route>,
  )

  const router = createBrowserRouter(routes, {
    basename: import.meta.env.VITE_BASE_URL,
  })

  return <RouterProvider router={router} />
}

export default function RetirectToLogin() {
  window.location.replace('https://localhost:44465/accountApi/processlogin')

  return <div>Router</div>
}
